import {
  formatMonthLabel,
  indexScheduleByDay,
  localCalendarDay,
  monthGrid,
  shiftMonth,
  yearMonthFromDay,
  type DayNote,
  type ScheduleEntry,
} from '@platewell/core';
import { useConnectionStatus, useDayNotes, useRecipes, useStore } from '@platewell/query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '../components/dialog.js';
import { RecipePickerDialog } from '../components/recipe-picker-dialog.js';
import { useMediaQuery } from '../hooks/use-media-query.js';
import { scheduleRoute, type ScheduleSearch } from '../router.js';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function SchedulePage() {
  const search = scheduleRoute.useSearch();
  const navigate = useNavigate();
  const recipes = useRecipes({ sort: 'title' });
  const dayNotes = useDayNotes();
  const store = useStore();
  const { readOnly } = useConnectionStatus();
  const today = localCalendarDay();
  const todayYm = yearMonthFromDay(today);
  const narrow = useMediaQuery('(width <= 900px)');

  const year = search.y ?? todayYm.year;
  const month = search.m ?? todayYm.month;

  const byDay = useMemo(() => indexScheduleByDay(recipes), [recipes]);
  const cells = useMemo(() => monthGrid(year, month), [year, month]);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  function goToMonth(next: { year: number; month: number }) {
    void navigate({
      to: '/schedule',
      search: monthSearch(next.year, next.month, todayYm),
    });
  }

  useEffect(() => {
    if (selectedDate != null) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target != null &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (document.querySelector('[role="dialog"]') != null) return;
      const key = event.key.toLowerCase();
      const next =
        event.key === 'ArrowLeft' || key === 'k'
          ? shiftMonth(year, month, -1)
          : event.key === 'ArrowRight' || key === 'j'
            ? shiftMonth(year, month, 1)
            : key === 't'
              ? todayYm
              : undefined;
      if (next == null) return;
      event.preventDefault();
      goToMonth(next);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedDate, year, month, todayYm.year, todayYm.month, navigate]);

  const selectedEntries = selectedDate == null ? [] : (byDay.get(selectedDate) ?? []);
  const selectedNotes = selectedDate == null ? [] : (dayNotes.get(selectedDate) ?? []);

  return (
    <>
      {narrow ? (
        <div className="schedule-header">
          <div className="schedule-heading">
            <h1>Schedule</h1>
            <span className="schedule-heading-month">{formatMonthLabel(year, month)}</span>
          </div>
          <span className="spacer" />
          <div className="schedule-month-nav">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => goToMonth(shiftMonth(year, month, -1))}
            >
              ←
            </button>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => goToMonth(shiftMonth(year, month, 1))}
            >
              →
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ marginBottom: 18 }}>
          <h1>Schedule</h1>
          <span className="spacer" />
          {(year !== todayYm.year || month !== todayYm.month) && (
            <button type="button" className="btn ghost" onClick={() => goToMonth(todayYm)}>
              Go to Today
            </button>
          )}
          <button
            type="button"
            className="btn ghost"
            aria-label="Previous month"
            onClick={() => goToMonth(shiftMonth(year, month, -1))}
          >
            ←
          </button>
          <span className="schedule-month-label">{formatMonthLabel(year, month)}</span>
          <button
            type="button"
            className="btn ghost"
            aria-label="Next month"
            onClick={() => goToMonth(shiftMonth(year, month, 1))}
          >
            →
          </button>
        </div>
      )}

      <div className="schedule-weekdays">
        {WEEKDAYS.map((day) => (
          <span key={day} className="schedule-weekday">
            {day}
          </span>
        ))}
      </div>

      <div className="schedule-grid">
        {cells.map((cell) => {
          const date = cell.date!;
          const entries = byDay.get(date) ?? [];
          const notes = dayNotes.get(date) ?? [];
          const itemCount = entries.length + notes.length;
          const isToday = date === today;
          const isSelected = date === selectedDate;
          return (
            <div
              key={date}
              role="button"
              tabIndex={0}
              className={[
                'schedule-cell',
                cell.inMonth ? '' : 'out-of-month',
                isToday ? 'today' : '',
                isSelected ? 'selected' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                setSelectedDate(date);
                setAdding(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedDate(date);
                  setAdding(false);
                }
              }}
            >
              <span className="schedule-day-num">{Number(date.slice(8))}</span>
              <ul className="schedule-titles">
                {entries.map((entry, index) => (
                  <li
                    key={entry.recipeId}
                    className={index >= 3 ? 'schedule-title-overflow' : undefined}
                  >
                    {/* A cell is too small to tap a title in on a phone, so there the whole
                        cell opens the day. */}
                    {narrow ? (
                      entry.title
                    ) : (
                      <Link
                        to="/recipes/$recipeId"
                        params={{ recipeId: entry.recipeId }}
                        className="schedule-title-link"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {entry.title}
                      </Link>
                    )}
                  </li>
                ))}
                {notes.map((note, index) => (
                  <li
                    key={note.id}
                    className={
                      entries.length + index >= 3
                        ? 'schedule-title-note schedule-title-overflow'
                        : 'schedule-title-note'
                    }
                  >
                    {note.text}
                  </li>
                ))}
                {itemCount > 3 && <li className="schedule-more">+{itemCount - 3} more</li>}
              </ul>
            </div>
          );
        })}
      </div>

      {selectedDate != null && adding && (
        <RecipePickerDialog
          title={formatDayHeading(selectedDate)}
          excludeIds={new Set(selectedEntries.map((entry) => entry.recipeId))}
          onClose={() => setAdding(false)}
          onPick={async (recipeId) => {
            await store.addCookDate(recipeId, selectedDate);
            setAdding(false);
          }}
          onAddNote={async (text) => {
            await store.addDayNote(selectedDate, text);
            setAdding(false);
          }}
        />
      )}

      {selectedDate != null && !adding && (
        <DayPanel
          date={selectedDate}
          entries={selectedEntries}
          notes={selectedNotes}
          readOnly={readOnly}
          onAdd={() => setAdding(true)}
          onClose={() => setSelectedDate(null)}
          onRemove={async (entry) => {
            for (const id of entry.cookDateIds) {
              await store.removeCookDate(entry.recipeId, id);
            }
          }}
          onRemoveNote={(note) => store.removeDayNote(note.id)}
        />
      )}
    </>
  );
}

function monthSearch(
  year: number,
  month: number,
  today: { year: number; month: number },
): ScheduleSearch {
  if (year === today.year && month === today.month) return {};
  return { y: year, m: month };
}

interface DayPanelProps {
  date: string;
  entries: readonly ScheduleEntry[];
  notes: readonly DayNote[];
  readOnly: boolean;
  onAdd: () => void;
  onClose: () => void;
  onRemove: (entry: ScheduleEntry) => Promise<void>;
  onRemoveNote: (note: DayNote) => Promise<void>;
}

function DayPanel({
  date,
  entries,
  notes,
  readOnly,
  onAdd,
  onClose,
  onRemove,
  onRemoveNote,
}: DayPanelProps) {
  const today = localCalendarDay();
  const isFuture = date > today;

  return (
    <Dialog onClose={onClose} className="schedule-day-panel" aria-label={formatDayHeading(date)}>
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>
          {formatDayHeading(date)}
          {date === today && <span className="hint"> · today</span>}
          {isFuture && <span className="hint"> · planned</span>}
        </h2>
      </div>

      {entries.length === 0 && notes.length === 0 ? (
        <p className="hint">No recipes on this day.</p>
      ) : (
        <ul className="schedule-day-list">
          {entries.map((entry) => (
            <li key={entry.recipeId} className="schedule-day-item">
              <Link
                to="/recipes/$recipeId"
                params={{ recipeId: entry.recipeId }}
                className="schedule-day-link"
              >
                {entry.title}
                {entry.cookDateIds.length > 1 && (
                  <span className="hint"> ×{entry.cookDateIds.length}</span>
                )}
              </Link>
              {!readOnly && (
                <button
                  type="button"
                  className="btn ghost danger"
                  onClick={() => void onRemove(entry)}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
          {notes.map((note) => (
            <li key={note.id} className="schedule-day-item">
              <span className="schedule-day-note">
                <span className="schedule-day-note-label">Note</span>
                {note.text}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="btn ghost danger"
                  onClick={() => void onRemoveNote(note)}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!readOnly && (
        <div style={{ marginTop: 16 }}>
          {/* Opening a day lands here, its main action. */}
          <button type="button" className="btn" autoFocus onClick={onAdd}>
            Add recipe
          </button>
        </div>
      )}
    </Dialog>
  );
}

function formatDayHeading(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const parsed = new Date(Date.UTC(y, m - 1, d));
  return parsed.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
